/**
 * The grid's pixel math — ONE definition of it (TV1-2291 · S1).
 *
 * Before this module the arithmetic lived twice, and the two copies disagreed:
 *
 *   WidgetLayoutEditor   colPx = Math.floor(( widthPx * scale ) / cols )   // 41
 *   WidgetLayoutViewer   colPx = ( widthPx * scale ) / cols                // 41.35
 *
 * At the shipped defaults (20 cols over 827px) that is a 0.35px-per-column
 * drift: the edit canvas rendered 820px wide against the viewer's 827px, and a
 * widget in the last column sat ~7px left of where the reader would see it.
 * Nothing was obviously broken, which is why it survived — a report simply did
 * not lay out quite the same in the two modes.
 *
 * The fractional form wins. Flooring throws away up to `cols - 1` pixels of
 * canvas and, once the container width stops being a round 827 (S2 makes it
 * fluid), the discarded remainder varies with the viewport — so a floored grid
 * would drift by a different amount at every window size.
 *
 * Every consumer goes through `computeGridGeometry`. Adding a term to the
 * geometry (a gutter, a fixed row height, a breakpoint's column count) is then
 * one edit here rather than a hunt for call sites that must agree.
 */

import { resolveGrid, type GridConfig } from './autoGrid';
import type { WidgetLayout } from './types';

/** A widget whose placement is concrete — what the auto-grid hands back. */
type PlacedWidget = { x: number; y: number; width: number; height: number };

/**
 * Row height, in CSS px — FIXED, not derived from the column width (S3).
 *
 * The engine used square cells (`rowPx = colPx`). That is invisible while the
 * canvas is pinned at 827px, and indefensible the moment it is fluid: on a
 * 1600px monitor a column becomes 80px, so a 5-row KPI tile that wants ~200px
 * would render 400px tall. Widening a viewport should make a chart WIDER, not
 * also twice as tall.
 *
 * 40px is chosen to sit just under the 41.35px the shipped 20-col/827px grid
 * resolved to, so existing reports keep their proportions (a 5-row tile goes
 * 207px → 200px) instead of jumping on the day this lands.
 */
export const ROW_HEIGHT_PX = 40;

export type GridGeometry = GridConfig & {
  /**
   * The report's own gutter, in CSS px — {@link WIDGET_GAP_PX} unless the layout
   * overrides it (2026-08-17).
   *
   * On the geometry rather than read from the layout at each call site, because
   * "how big is a cell" and "how much of it is gutter" are the same question asked
   * of the same object, and splitting them is how a renderer ends up drawing at one
   * gap while the minimum-width maths assumes another.
   */
  gapPx: number;
  /** Width of one column, in CSS px. Fractional on purpose — see the module docblock. */
  colPx: number;
  /** Height of one row, in CSS px. Fixed — see `ROW_HEIGHT_PX`. */
  rowPx: number;
  /** Width of the whole canvas, in CSS px. */
  canvasWidthPx: number;
};

export type GeometryOptions = {
  /** Toolbar zoom. Transient; never persisted. */
  scale?: number;
  /**
   * Measured width of the element the canvas renders into. When absent the
   * layout's own `widthPx` is used, which is the pre-S2 fixed-canvas behaviour.
   */
  containerWidthPx?: number;
};

export const computeGridGeometry = (
  layout: WidgetLayout,
  { scale = 1, containerWidthPx }: GeometryOptions = {}
): GridGeometry => {
  const grid = resolveGrid( layout );
  // `containerWidthPx` is only honoured when it is a real measurement. A
  // ResizeObserver reports 0 for an element that has not been laid out yet (and
  // for a hidden one), and a 0-width canvas divides down to `colPx = 0`, which
  // collapses every widget to a zero-size box on the first paint. Falling back
  // to the layout's own `widthPx` also keeps a non-DOM caller (a test, an SSR
  // pass) rendering at the historical fixed size.
  const basis = containerWidthPx && containerWidthPx > 0 ? containerWidthPx : grid.widthPx;
  const canvasWidthPx = basis * scale;
  const colPx = canvasWidthPx / grid.cols;

  // Rows do NOT scale with the viewport — only columns do. Zoom still applies:
  // it is a magnification of the whole canvas, not a re-layout.
  // Clamped to the same 0-48 the domain schema declares, and NaN/absent falls back
  // to the default — a persisted layout can carry anything, and a negative gutter
  // would draw cards larger than their cells and overlap the neighbours.
  const declaredGap = ( layout as { gap?: number }).gap;
  const gapPx = Number.isFinite( declaredGap )
    ? Math.min( 48, Math.max( 0, declaredGap as number ))
    : WIDGET_GAP_PX;

  return { ...grid, colPx, rowPx: ROW_HEIGHT_PX * scale, canvasWidthPx, gapPx };
};

/**
 * The GUTTER between cards, in CSS px (2026-08-17).
 *
 * Cards used to be drawn flush to their grid cells, so two neighbours shared an
 * edge and a report read as one dense slab rather than a set of cards.
 *
 * ⚠️ It is a RENDERING inset, deliberately NOT part of the grid maths. The cell
 * size, the drag snapping, the collision tests and everything persisted stay on
 * whole cells — a gutter that changed the geometry would make a card's stored
 * width mean something different at every gap value, and re-flow every existing
 * report the moment the number was tuned. Each card is simply drawn `GAP` px
 * smaller than the cell it owns, centred in it, which produces a uniform gutter
 * between neighbours and a half-gutter at the canvas edge.
 *
 * Exposed as a constant rather than inlined so the viewer's rect maths and the
 * editor's CSS agree by construction; the editor applies it through the
 * `--widget-gap` custom property (its cards are sized by CSS, not by this
 * function — the `<Rnd>` keeps the full cell so dragging still snaps to it).
 */
export const WIDGET_GAP_PX = 12;

/**
 * Where a resolved widget is DRAWN, in CSS px — the cell, inset by the gutter.
 *
 * `gap` is a parameter with a default rather than a hard-coded read of
 * {@link WIDGET_GAP_PX} so a caller that needs the raw cell (a test asserting the
 * grid maths, a future print layout that wants no gutter) can ask for it without
 * this function growing a second name.
 */
export const widgetRectPx = (
  widget: PlacedWidget,
  geometry: GridGeometry,
  // Defaults to the REPORT's gutter, not the module constant — so a per-report gap
  // reaches the drawn rect without every call site remembering to pass it. An
  // explicit `0` is still honoured, which is what the grid-maths tests use.
  gap: number = geometry.gapPx ?? WIDGET_GAP_PX
) => ({
  left: widget.x * geometry.colPx + gap / 2,
  top: widget.y * geometry.rowPx + gap / 2,
  // `Math.max( 0, … )` so a one-cell-tall widget on a small row height cannot be
  // given a negative box; it collapses to zero instead of inverting.
  width: Math.max( 0, widget.width * geometry.colPx - gap ),
  height: Math.max( 0, widget.height * geometry.rowPx - gap )
});

/**
 * Height the canvas needs to hold every widget, plus `extraRows` of slack so
 * there is somewhere to drag a widget TO at the bottom.
 *
 * The editor has always asked for 2 rows of slack and the viewer for 1 — the
 * editor needs drop room, the reader does not. That difference is deliberate,
 * so it stays a parameter rather than becoming a constant here.
 */
export const canvasHeightPx = (
  widgets: readonly PlacedWidget[],
  geometry: GridGeometry,
  extraRows: number
): number => {
  const bottomRow = widgets.length
    ? Math.max( ...widgets.map(( w ) => w.y + w.height ))
    : 0;
  return ( bottomRow + extraRows ) * geometry.rowPx;
};

/**
 * Snap a pixel offset back to a grid cell.
 *
 * Mirrors what `onDragStop` / `onResizeStop` have always done by hand, so the
 * clamping rules live next to the geometry that produced the pixels rather than
 * being re-derived at each react-rnd callback.
 */
export const pxToCol = ( px: number, geometry: GridGeometry, span: number ): number =>
  Math.min( Math.max( 0, Math.round( px / geometry.colPx )), Math.max( 0, geometry.cols - span ));

export const pxToRow = ( px: number, geometry: GridGeometry ): number =>
  Math.max( 0, Math.round( px / geometry.rowPx ));

export const pxToSpanX = ( px: number, geometry: GridGeometry ): number =>
  Math.max( 1, Math.round( px / geometry.colPx ));

export const pxToSpanY = ( px: number, geometry: GridGeometry ): number =>
  Math.max( 1, Math.round( px / geometry.rowPx ));
